import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Our intuitive understanding of distance fails us",
  "description": "",
  "published": "2011-09-06T00:00:00.000Z",
  "redirect_from": ["/blog/our-intuitive-understanding-of-distance-fails-us/swizec/2368"],
  "categories": "Distance, Geometry, Graph Drawing, Math, Right triangle, Shortest path problem, Uncategorized, United States",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/09/photo-150x150.jpg",
        "alt": null,
        "title": "Distance vs. cost"
      }}></img></p>
    <p>{`This is a debate I keep getting into with all sorts of people. No matter how well I try explaining it people simply do not want to believe me they're thinking about `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Distance",
        "title": "Distance"
      }}>{`distance`}</a>{` all wrong.`}</p>
    <p>{`After last weekend's trek around a very sunny city where I was forced to keep walking in the blistering noon sun, just because "It's closer!" and my sister is a naggy teenager and gets her way, I've decided to write this post wherein I explain once and for all the difference between intuitive distance and real-world distance.`}</p>
    <h2 {...{
      "id": "intuitive-distance"
    }}>{`Intuitive distance`}</h2>
    <p>{`First let's talk a little bit about how all of us think of optimizing our paths for distance.`}</p>
    <p>{`You've probably noticed bits of grass so destroyed around the corners of walkways in parks that eventually the walkways are just expanded with rounded corners. Some architects are even so smart as to design them this way in the first place!`}</p>
    <p>{`This is due to how `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Right_triangle",
        "title": "Right triangle"
      }}>{`right triangles`}</a>{` work. The rule is basically that for any right triangle the length of the hypotenuse will necessarily be shorter than the sum of its catheti.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/09/300px-Distancedisplacement.svg_4.png",
        "alt": "Distance vs. displacement",
        "title": "Distance vs. displacement"
      }}></img></p>
    <p>{`You can quickly check this is true with the first `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Pythagorean_triple",
        "title": "Pythagorean triple"
      }}>{`pythagorean triplet`}</a>{` (3,4,5). Obviosly 5 is less than 3+4. Far from a rigorous proof, but the idea isn't stupid.`}</p>
    <p>{`This is something that comes very intuitively to us. See a right triangle and choose to walk diagonally, it's simply shorter that way.`}</p>
    <p>{`It's even more obvious when you think about it in terms of distance traveled versus displacement. The `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Shortest_path_problem",
        "title": "Shortest path problem"
      }}>{`shortest path`}</a>{` to take between two points will always be one that best fits displacement.`}</p>
    {
      /*more*/
    }
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/09/283px-Manhattan_distance.svg_.png",
        "alt": "Figure illustrating Manhattan versus Euclidean...",
        "title": "Figure illustrating Manhattan versus Euclidean..."
      }}></img></p>
    <h2 {...{
      "id": "taxicab-geometry"
    }}><a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Taxicab_geometry",
        "title": "Taxicab geometry"
      }}>{`Taxicab Geometry`}</a></h2>
    <p>{`All of this comes crashing to a halt in large american cities. Those tend to be laid out in a grid, and prevent us from traveling over proper `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Diagonal",
        "title": "Diagonal"
      }}>{`diagonals`}</a>{`. Instead we follow diagonals to our best abilities alongside a grid.`}</p>
    <p><a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Hermann_Minkowski",
        "title": "Hermann Minkowski"
      }}>{`Herman Minkowski`}</a>{` stipulated in the 19th century that because of this constraint, the distance traveled diagonally is no different than the distance traveled on any other gridded path with the same displacement.`}</p>
    <p>{`By looking at the sketch, I think we can all agree with Minkowski.`}</p>
    <h2 {...{
      "id": "distance-as-a-parameter-of-cost"
    }}>{`Distance as a parameter of cost`}</h2>
    <p>{`But taxicab geometry only works in `}<a parentName="p" {...{
        "href": "http://maps.google.com/maps?ll=38.8833333333,-77.0166666667&spn=10.0,10.0&q=38.8833333333,-77.0166666667%20(United%20States)&t=h",
        "title": "United States"
      }}>{`the US`}</a>{` and other cities laid out in a grid, I haven't really seen many of them. So maybe in Europe our intuition still works?`}</p>
    <p>{`Everything becomes much more interesting once we start thinking of distance as a parameter of the function of cost.`}</p>
    <p>{`Imagine you have to travel across a city. You can take a very short path through the streets. This path runs on smaller roads, which have a lot of crossroads, many red lights, people waiting to turn left and so on. Another path, a much longer one, runs mostly on larger roads where you have fewer stop lights and everyone waiting to turn left is ushered into their own lane.`}</p>
    <p>{`Which path do you take?`}</p>
    <p>{`It's probably obvious, but I've been in plenty of fights for taking the longer route.`}</p>
    <p>{`The whole problem boils down to something computer scientists and probably most mathematicians call graph traversal and finding the shortest path through a graph.`}</p>
    <p>{`Basically it goes like this: You have a set of nodes connected with edges. Each edge has a number assigned to it. Find the path from node A to node B with the smallest total sum of traversed edges.`}</p>
    <p>{`That number is completely abstract, it can mean distance or can be a function of many parameters telling you how costly it is to travel alongside that edge; it essentially doesn't matter, whatever you want to optimize for, that's how you define the function.`}</p>
    <p>{`When walking I like to account for amount of sunlight ... especially in the summer.`}</p>
    <p>{`There are many ways to approach solving this problem, but the algorithm `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Dijkstra%27s_algorithm",
        "title": "Dijkstra's algorithm"
      }}>{`Dijkstra`}</a>{` came up with seems to work the best in most cases.`}</p>
    <p>{`To illustrate my point, look at the pretty picture I've drawn on my whiteboard. Imagine this is a map of a few streets - `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Euclidean_distance",
        "title": "Euclidean distance"
      }}>{`geometric distance`}</a>{` accounts for our `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Intuition_%28knowledge%29",
        "title": "Intuition (knowledge)"
      }}>{`intuitive understanding`}</a>{` of distance. The cost I've chosen to optimize for is time.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/09/photo-1024x764.jpg",
        "alt": null,
        "title": "Distance vs. cost"
      }}></img></p>
    <p>{`Point proven? Or at least shown? Can I just send this link every time I get into this discussion?`}</p>
    <p>{`But the most interesting bit, I think, is how a lot of people think like this in a car. Why not on foot? Or on a bike? What makes those ways of traveling so much different that suddenly physical distance traveled is the only factor we think about? No matter how many things I have to climb over, I will fucking walk the shortest path!`}</p>
    <p>{`Really?`}</p>
    <p>{`PS: another interesting example is the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/MythBusters_(2010_season)#Episode_145_.E2.80.93_Waterslide_Wipeout"
      }}>{`Mythbusters Waterslide Wipeout`}</a>{` episode, where they show a mail truck doing only right turns (in the US) saves fuel despite traveling a much greater physical distance.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://harablog.wordpress.com/2011/08/26/fast-pathfinding-via-symmetry-breaking/"
        }}>{`Fast Pathfinding via Symmetry Breaking`}</a>{` (harablog.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://math.stackexchange.com/questions/154/do-complex-numbers-really-exist"
        }}>{`Do complex numbers really exist?`}</a>{` (math.stackexchange.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://news.bioscholar.com/2011/08/our-brain-fails-to-map-high-distances.html"
        }}>{`Our brain fails to map high distances`}</a>{` (news.bioscholar.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://mindblog.dericbownds.net/2011/06/innate-euclidean-geometry.html"
        }}>{`Innate Euclidean geometry.`}</a>{` (mindblog.dericbownds.net)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=d4819b46-01e9-4663-ac97-55b0fbfa68c0",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      